// ---------------------------------------------------------------------------
// LocalWasmRepository — CogoKnowledgeRepository impl over @cogo/kg-browser.
//
// Phase 1b Step 1b.3 per .planning/kg-repository-interface.md v1.0 §12.5.
//
// Design points:
//   - Reads are SPARQL over Oxigraph WASM. Observation writes
//     (`recordObservation` / `syncObservations` / `saveObservations` /
//     `findObservations`) hold MVP in-memory + cogo-kg-worker durable copy
//     — Phase 2c Data Symbol will add a local SPARQL INSERT path. Catalog
//     writes (`registerExample` / `upsertDesignSystem`) stay server-only
//     by design and throw on the local path.
//   - Init lifecycle mirrors Phase 1a kg-client.ts ensureKgReady: idempotent
//     lazy dynamic import + manifest fetch + TTL hydrate. Repository owns
//     the state; consumers call `ready()` or any read method to trigger.
//   - SPARQL property names align with cogo-ns-v1.ttl 1:1.
//   - Validation = store-scoped SHACL per §4a: Repository exposes
//     {@link StoreScopedValidation} so callers can loadJsonLd + dropGraph
//     around `validate(phase)` calls.
//
// This file has zero React / Studio dependencies. Studio continues to own
// the KgProvider + useKg hook (Step 1b.6 refactors Studio to consume this
// Repository instead of the Phase 1a kg-client.ts).
// ---------------------------------------------------------------------------

import type {
  InitResult,
  ShaclQueryRule,
  ShaclReport,
  Subscription as RealtimeSubscription,
} from "@cogo/kg-browser"
import { subscribeToTenantOntology } from "@cogo/kg-browser"

import type {
  Action,
  DesignSystem,
  Event,
  Example,
  FindObservationsQuery,
  I18n,
  Narrative,
  Observation,
  ObservationRecord,
  Pattern,
  Result,
  SaveResult,
  SemanticType,
  SyncResult,
  ThemeV2Record,
  Tokens,
  ValidationPhase,
  ValidationReport,
} from "./types"

import type {
  CogoKnowledgeRepository,
  FindExamplesQuery,
  GetActionsFilter,
  GetEventsFilter,
  ShaclRuleRegistry,
  StoreScopedValidation,
} from "./repository"

import {
  makeClientSessionId,
  observationToNQuads,
} from "./observations/serialize"

import {
  EXPRESSION_ACCESSOR_SPARQL,
  EXPRESSION_PREFIX_SPARQL,
  UI_ACTION_VOCABULARY_SPARQL,
  UI_EVENT_VOCABULARY_SPARQL,
  mapExpressionAccessorRows,
  mapExpressionPrefixRows,
  mapUiActionVocabularyRows,
  mapUiEventVocabularyRows,
  type ExpressionAccessorItem,
  type ExpressionPrefixItem,
  type UiActionVocabularyItem,
  type UiEventVocabularyItem,
} from "./vocabulary"

// ---------------------------------------------------------------------------
// Config + supporting types
// ---------------------------------------------------------------------------

export interface LocalWasmRepositoryConfig {
  /**
   * CDN manifest URL. Example: "https://cogo-starter.pages.dev/kg/manifest.json".
   * Caller resolves env/flags; Repository stays framework-agnostic.
   */
  manifestUrl: string
  /**
   * Optional SHACL rule registry. If absent, `validate(phase)` returns an
   * empty report. Phase 1a DEFAULT_RULES can be wrapped via a trivial
   * registry impl (see §12.4 Q9).
   */
  ruleRegistry?: ShaclRuleRegistry
  /** Passed through to initKgCore. */
  verbose?: boolean
  /**
   * Optional Cloudflare Worker base URL for observation sync/save.
   * e.g., `https://cogo-kg-worker-preview.<account>.workers.dev`.
   * If absent, syncObservations / saveObservations throw.
   */
  workerBaseUrl?: string
}

/**
 * Phase 440 Plan 04 RUNTIME-04 — options for
 * {@link LocalWasmRepository.subscribeToTenantInvalidation}.
 *
 * The repository's invalidation subscription delegates to
 * {@link subscribeToTenantOntology} from `@cogo/kg-browser`, which itself
 * wraps Phase 0439 REALTIME-01's `subscribeOntologyChanges` (channel name
 * `ontology:${tenantId}:${appId}`, server-side `tenant_id=eq` filter +
 * client-side `app_id` secondary filter).
 *
 * Auth: callers may either pass an existing supabaseClient via the
 * underlying API directly, OR pass `supabaseUrl` + `anonKey` here and the
 * wrapper builds a fresh client. Per Plan 04 PLAN.md interfaces block.
 */
export interface SubscribeToTenantOpts {
  readonly tenantId: string
  readonly appId: string
  readonly branch?: string
  /** Project URL for Realtime subscription (e.g., https://<ref>.supabase.co). */
  readonly supabaseUrl?: string
  /** Anon (or authenticated user) JWT for the Realtime channel. */
  readonly anonKey?: string
}

interface KgBrowserModule {
  initKgCore: (opts?: unknown) => Promise<InitResult>
  queryShape: (sparql: string) => {
    rows: Array<Record<string, string>>
    count: number
    durationMs: number
  }
  applyShacl: (rules: ShaclQueryRule[]) => ShaclReport
  versionInfo: () => {
    coreVersion: string
    oxigraphVersion: string
    shapeSha: string
  }
  isReady: () => boolean
  loadJsonLdIntoNamedGraph: (
    jsonLd: string,
    graphIri: string,
  ) => { graphIri: string; triples: number; durationMs: number }
  dropNamedGraph: (graphIri: string) => { removed: number; durationMs: number }
}

interface ManifestEntry {
  layer: string
  name: string
  url: string
  sha256: string
}

interface Manifest {
  cdnBase: string
  entries: ManifestEntry[]
}

// ---------------------------------------------------------------------------
// SPARQL templates (aligned with cogo-ns-v1.ttl — see §4 of design doc)
// ---------------------------------------------------------------------------

const NS = "PREFIX cogo: <https://cogo.xyz/ns/v1#>\n"
const RDFS = "PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>\n"

// L259-v2 fix (Phase 7004.X, 2026-05-13): map kebab CoboPattern → cobo: TBox
// URI suffix, used by findExamples to issue a UNION SPARQL covering both
// examples-v1 (`cogo:patternRef cpat:<kebab>`) AND cobo ABox
// (`rdf:type cobo:<CamelCase>Pattern`). PR #469 fix only addressed the
// `isCoboPattern` gate inside `retrieveCoboCandidates`; the underlying
// SPARQL still missed all 149 cobo blocks because they lack `cogo:patternRef`
// triples. The 5-entry table is duplicated from
// `packages/cogo-agentic-generator/src/cobo/cobo-pattern-resolver.ts`
// (`TBOX_PATTERN_TO_KEBAB` inverse) to avoid a circular dependency
// (kg-repository → agentic-generator → kg-repository). Sync this table
// when adding/removing CoboPattern members. See
// `.planning/cogo-commerce-conversion/phases/0005-rag-generator-mvp/M5-4-cross-pattern-measurement/L259-V2-INVESTIGATION.md`.
const COBO_NS_URI = "https://cogo.xyz/ns/v1/commerce-backoffice#"
const KEBAB_TO_COBO_TBOX: Readonly<Record<string, string>> = {
  "list-view": "ListViewPattern",
  "detail-view": "DetailViewPattern",
  "kpi-dashboard": "KpiDashboardPattern",
  "form-submit": "FormSubmitPattern",
  "chart-view": "ChartViewPattern",
}
function kebabToCoboTboxName(kebab: string | undefined): string | undefined {
  if (!kebab) return undefined
  return KEBAB_TO_COBO_TBOX[kebab]
}

// Caller-controlled iri/string sanitizers.
function sanitizeIri(iri: string): string {
  return iri.replace(/[<>"]/g, "")
}
function sanitizeLiteral(s: string): string {
  return s.replace(/"/g, '\\"')
}

// ---------------------------------------------------------------------------
// Helper — empty ShaclRuleRegistry
// ---------------------------------------------------------------------------

export const EMPTY_RULE_REGISTRY: ShaclRuleRegistry = {
  rulesFor(_phase: ValidationPhase) {
    return []
  },
}

function mintScopeIri(): string {
  const g = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  const id =
    g && typeof g.randomUUID === "function"
      ? g.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  return `urn:cogo:scope:${id}`
}

/**
 * Resolve a manifest URL into a `URL` instance, accepting either:
 *   1. An absolute URL (`https://host/kg/manifest.json`) — used as-is.
 *   2. A relative URL (`/kg/manifest.json`, `kg/manifest.json`) — resolved
 *      against the supplied `locationHref` base (typically
 *      `window.location.href`).
 *
 * Exported for unit testing; call site is `LocalWasmRepository.ensure()`.
 *
 * Rationale: same-origin Studio deployments should be able to ship
 * `manifestUrl: "/kg/manifest.json"` without baking the deploy host into
 * the bundle, which previously failed because `new URL(relative)` throws.
 */
export function resolveManifestBase(
  manifestUrl: string,
  locationHref?: string,
): URL {
  if (locationHref) {
    return new URL(manifestUrl, locationHref)
  }
  return new URL(manifestUrl)
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export class LocalWasmRepository
  implements CogoKnowledgeRepository, StoreScopedValidation
{
  private mod: KgBrowserModule | null = null
  private initResult: InitResult | null = null
  private initPromise: Promise<InitResult | null> | null = null
  private readonly rules: ShaclRuleRegistry
  // Gap 2 MVP: in-memory pending buffer. Server-side Oxigraph (Phase A Worker)
  // holds the durable copy. Local SPARQL INSERT deferred until KgBrowserModule
  // exposes an update() API — reads keep working off worker-synced shards.
  private pendingObservations: ObservationRecord[] = []

  constructor(private readonly config: LocalWasmRepositoryConfig) {
    this.rules = config.ruleRegistry ?? EMPTY_RULE_REGISTRY
  }

  // ——— Lifecycle ———

  /** Resolves when Oxigraph is loaded + shards hydrated. Idempotent. */
  async ready(): Promise<void> {
    await this.ensure()
  }

  /** Returns true when initKgCore has resolved. Reads module state directly. */
  isReady(): boolean {
    return !!this.initResult && !!this.mod && this.mod.isReady()
  }

  versionInfo(): {
    coreVersion: string
    oxigraphVersion: string
    shapeSha: string
  } {
    if (!this.mod) {
      throw new Error(
        "[@cogo/kg-repository] versionInfo called before ready() — call repo.ready() first",
      )
    }
    return this.mod.versionInfo()
  }

  /**
   * Phase 440 Plan 04 RUNTIME-04 — subscribe to per-tenant ontology change
   * events via Phase 439 Supabase Realtime (REALTIME-01 shipped at
   * cogo-platform commit `8a4482c1`). Delegates to
   * {@link subscribeToTenantOntology} from `@cogo/kg-browser`.
   *
   * On each `cogo.application_ontology` row event matching `(tenantId, appId,
   * branch)`, the configured callback fires. Repository-side cache eviction
   * for the matching key is the caller's responsibility — LocalWasmRepository
   * does not currently maintain a per-tenant partition (Phase 440 Plan 02
   * lives on the cogo-platform side; the cogo-web repository may grow a
   * matching client-side partition in a follow-up phase). For now, the
   * default behaviour is a verbose-gated `console.debug` for visibility plus
   * the repository's `_lastInvalidationEvent` field (test seam).
   *
   * Threat T-440-04-03 (DoS via unbounded WebSocket subscriptions): the
   * returned Subscription has `unsubscribe()` and the caller MUST invoke it
   * on teardown.
   *
   * @param opts (tenantId, appId, branch?, supabaseUrl?, anonKey?)
   * @returns Subscription with `unsubscribe()` (idempotent).
   */
  subscribeToTenantInvalidation(
    opts: SubscribeToTenantOpts,
  ): { unsubscribe: () => void } {
    const subscription: RealtimeSubscription = subscribeToTenantOntology({
      tenantId: opts.tenantId,
      appId: opts.appId,
      branch: opts.branch ?? "main",
      supabaseUrl: opts.supabaseUrl,
      anonKey: opts.anonKey,
      onInvalidate: (payload) => {
        this._lastInvalidationEvent = payload
        if (this.config.verbose) {
          // eslint-disable-next-line no-console
          console.debug(
            `[LocalWasmRepository] tenant ${opts.tenantId}:${opts.appId}` +
              `:${payload.branch} invalidated (${payload.eventType} ${payload.subject})` +
              ` — cache eviction NOT YET WIRED at the repository layer; ` +
              `Phase 440 Plan 02 lives on cogo-platform side.`,
          )
        }
      },
    })
    return { unsubscribe: subscription.unsubscribe }
  }

  /**
   * Test seam — last invalidation event observed by
   * {@link subscribeToTenantInvalidation}. Production callers should not
   * read this; tests use it to assert end-to-end flow without subscribing
   * a separate listener.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _lastInvalidationEvent: any = null

  private async ensure(): Promise<InitResult | null> {
    if (this.initResult) return this.initResult
    if (this.initPromise) return this.initPromise

    this.initPromise = (async () => {
      try {
        const mod = (await import(
          "@cogo/kg-browser"
        )) as unknown as KgBrowserModule
        this.mod = mod

        const manifestRes = await fetch(this.config.manifestUrl)
        if (!manifestRes.ok) {
          throw new Error(
            `[kg-repository] manifest fetch ${manifestRes.status} ${manifestRes.statusText} — ${this.config.manifestUrl}`,
          )
        }
        const manifest = (await manifestRes.json()) as Manifest

        const locationHref = (
          globalThis as { location?: { href?: string } }
        ).location?.href
        const base = resolveManifestBase(
          this.config.manifestUrl,
          locationHref,
        )
        const urlFor = (entry: { url: string }) =>
          new URL(entry.url, base).toString()

        const wasmEntry = manifest.entries.find((e) => e.layer === "L0")
        const ttlEntries = manifest.entries.filter(
          (e) => e.layer !== "L0" && e.layer !== "L1b",
        )

        const ttlSources = await Promise.all(
          ttlEntries.map(async (e) => {
            const res = await fetch(urlFor(e))
            if (!res.ok) {
              throw new Error(
                `[kg-repository] ttl fetch failed ${e.name}: ${res.status}`,
              )
            }
            return { name: e.name, ttl: await res.text() }
          }),
        )

        const shapesSource = ttlSources.find(
          (t) => t.name === "cogo-ns-v1.ttl",
        )

        const result = await mod.initKgCore({
          wasmUrl: wasmEntry ? urlFor(wasmEntry) : undefined,
          ttlSources,
          shapesTtl: shapesSource?.ttl,
          verbose: this.config.verbose ?? false,
        })
        this.initResult = result
        return result
      } catch (err) {
        this.mod = null
        this.initResult = null
        throw err
      }
    })()

    return this.initPromise
  }

  // ——— Store-scoped validation helpers (Option A) ———

  async loadJsonLd(jsonLd: unknown, graphIri?: string): Promise<string> {
    await this.ensure()
    if (!this.mod) {
      throw new Error(
        "[@cogo/kg-repository] loadJsonLd called without init — ensure() did not resolve",
      )
    }
    const iri = graphIri ?? mintScopeIri()
    const payload =
      typeof jsonLd === "string" ? jsonLd : JSON.stringify(jsonLd ?? {})
    this.mod.loadJsonLdIntoNamedGraph(payload, iri)
    return iri
  }

  async dropGraph(graphIri: string): Promise<void> {
    await this.ensure()
    if (!this.mod) {
      throw new Error(
        "[@cogo/kg-repository] dropGraph called without init — ensure() did not resolve",
      )
    }
    this.mod.dropNamedGraph(graphIri)
  }

  // ——— Read methods ———

  async query(sparql: string): Promise<{
    rows: Array<Record<string, string>>
    durationMs: number
  }> {
    await this.ensure()
    if (!this.mod) {
      throw new Error(
        "[@cogo/kg-repository] query called without init — ensure() did not resolve",
      )
    }
    const out = this.mod.queryShape(sparql)
    return { rows: out.rows, durationMs: out.durationMs }
  }

  async findExamples(
    q: FindExamplesQuery,
  ): Promise<Result<Example[]>> {
    const k = q.k ?? 5
    // At least one of semanticType or pattern is required — the examples-v1
    // shard needs an index anchor. An unanchored query would walk all 229
    // instances and is too expensive for a hot path.
    if (!q.semanticType && !q.pattern) {
      return {
        data: [],
        meta: { durationMs: 0, source: "local" },
      }
    }
    const clauses: string[] = [`?ex a cogo:Example .`]
    if (q.semanticType) {
      const semTypeIri = sanitizeIri(q.semanticType)
      clauses.push(`?ex cogo:semanticType <${semTypeIri}> .`)
    }
    if (q.pattern) {
      // Accept a bare short id ("auth-form") OR a full cpat: IRI — expand
      // the short form so block-level callers can pass category-derived
      // pattern labels without constructing IRIs themselves.
      const patIri = q.pattern.includes("://")
        ? sanitizeIri(q.pattern)
        : `https://cogo.xyz/instances/pattern/${sanitizeIri(q.pattern)}`
      clauses.push(`?ex cogo:patternRef <${patIri}> .`)
    }
    const dsFilter = q.dsId
      ? `FILTER (?ds = "${sanitizeLiteral(q.dsId)}")`
      : ""

    // L259-v2 fix (Phase 7004.X, 2026-05-13): when q.pattern matches a
    // CoboPattern kebab, also query the cobo ABox (rdf:type cobo:<CamelCase>).
    // PR #469 fixed `isCoboPattern` rejection of CamelCase but the SPARQL
    // still only matched examples-v1 `cogo:patternRef` triples — 0 of 149
    // cobo blocks have that predicate. Without this UNION, ranker probes
    // against KpiDashboard/ListView/etc. return empty for production cobo
    // retrieval. See L259-V2-INVESTIGATION.md.
    //
    // The cobo branch is omitted (regression-clean) when:
    //   - q.pattern is unset OR is a full IRI (not a CoboPattern kebab)
    //   - q.pattern is a non-cobo kebab (e.g., "auth-form", "cart-summary")
    //   - q.semanticType is set (cobo blocks lack cogo:semanticType; the
    //     UNION branch wouldn't match anyway, kept omitted for SPARQL hygiene)
    const coboTboxName =
      q.pattern && !q.pattern.includes("://") && !q.semanticType
        ? kebabToCoboTboxName(q.pattern)
        : undefined
    // ORDER BY DESC(?score) DESC(?qg) ?ex makes retrieval deterministic so a downstream
    // picker can take the top-scored example without an LLM. ?score is OPTIONAL
    // (cobo-UNION rows lack it → unbound sorts last in DESC); DESC(?qg) (cogo:qualityGrade,
    // R1 rendered grade) breaks the score-90 plateau by MEASURED quality
    // instead of IRI spelling — the id-ASC-only tiebreak let alphabetically
    // early families monopolize every LIMIT window (k-window starvation,
    // 2026-07-29); ungraded rows sort after graded at equal score. The ?ex
    // final key keeps order stable across engine runs.
    const sparql = coboTboxName
      ? `${NS}PREFIX cobo: <${COBO_NS_URI}>
      SELECT ?ex ?ds ?bschema ?tmpl ?blockJson ?semType ?score ?qg ?narr ?sh ?sr WHERE {
        {
          ${clauses.join("\n          ")}
          OPTIONAL { ?ex cogo:semanticType ?semType }
          OPTIONAL { ?ex cogo:designSystemId ?ds }
          OPTIONAL { ?ex cogo:bindingSchema  ?bschema }
          OPTIONAL { ?ex cogo:template       ?tmpl }
          OPTIONAL { ?ex cogo:blockJson      ?blockJson }
          OPTIONAL { ?ex cogo:score          ?score }
          OPTIONAL { ?ex cogo:qualityGrade   ?qg }
          OPTIONAL { ?ex cogo:narrativeSummary ?narr }
          OPTIONAL { ?ex cogo:structuralHash     ?sh }
          OPTIONAL { ?ex cogo:structuralReplicas ?sr }
          ${dsFilter}
        } UNION {
          ?ex a cobo:${coboTboxName} .
          OPTIONAL { ?ex cogo:designSystemId ?ds }
        }
      }
      ORDER BY DESC(?score) DESC(?qg) ?ex
      LIMIT ${k}
    `
      : `${NS}
      SELECT ?ex ?ds ?bschema ?tmpl ?blockJson ?semType ?score ?qg ?narr ?sh ?sr WHERE {
        ${clauses.join("\n        ")}
        OPTIONAL { ?ex cogo:semanticType ?semType }
        OPTIONAL { ?ex cogo:designSystemId ?ds }
        OPTIONAL { ?ex cogo:bindingSchema  ?bschema }
        OPTIONAL { ?ex cogo:template       ?tmpl }
        OPTIONAL { ?ex cogo:blockJson      ?blockJson }
        OPTIONAL { ?ex cogo:score          ?score }
        OPTIONAL { ?ex cogo:qualityGrade   ?qg }
        OPTIONAL { ?ex cogo:narrativeSummary ?narr }
        OPTIONAL { ?ex cogo:structuralHash     ?sh }
        OPTIONAL { ?ex cogo:structuralReplicas ?sr }
        ${dsFilter}
      }
      ORDER BY DESC(?score) DESC(?qg) ?ex
      LIMIT ${k}
    `
    const { rows, durationMs } = await this.query(sparql)
    const data: Example[] = rows.map((row) => ({
      id: row.ex,
      // Prefer the shard's typed semanticType edge when available so
      // pattern-only callers still receive the canonical type. Fall back
      // to the query-supplied value for semanticType-only callers so the
      // contract shape stays stable across both query forms.
      semanticType: row.semType ?? q.semanticType ?? "",
      dsId: row.ds || undefined,
      bindingSchema: row.bschema ? this.safeParseJson(row.bschema) : undefined,
      // v1.0.9: cogo:template is populated on 223/229 instances (Rewriter
      // output with slot expressions). The 6 template-less instances
      // return undefined here — Studio/agentic consumers MUST null-check.
      template: row.tmpl ? this.safeParseJson(row.tmpl) : undefined,
      // Raw mined block body (pre-Rewriter). Shipped alongside template
      // so Studio can diff source vs templated form; few-shot exemplars
      // continue to use blockJson.
      blockJson: row.blockJson ? this.safeParseJson(row.blockJson) : undefined,
      // cogo:score [0,100] — the deterministic ranking key. "0" is a valid
      // truthy string so this preserves a real zero; unbound/empty → undefined.
      score: row.score ? Number(row.score) : undefined,
      // cogo:qualityGrade [0,100] — R1 rendered design-quality grade; SEPARATE from
      // score. The deterministic picker uses it as a within-family tiebreak BELOW
      // family/intent match. Unbound (ungraded block) → undefined → -∞ in the picker.
      qualityGrade: row.qg ? Number(row.qg) : undefined,
      // cogo:narrativeSummary — discriminating signal for the R3 intent-match
      // tiebreak (block node types are generic primitives; the narrative is not).
      narrativeSummary: row.narr || undefined,
      // cogo:structuralHash — R1 structural identity; lets the picker read instead of
      // recomputing. Absent on pre-field corpus → undefined → picker recomputes.
      structuralHash: row.sh || undefined,
      // cogo:structuralReplicas — R1 structural multiplicity; the picker SUMS it for the
      // modal-cluster tiebreak. Absent → undefined → treated as 1 (legacy row-count).
      structuralReplicas: row.sr ? Number(row.sr) : undefined,
    }))
    return { data, meta: { durationMs, source: "local" } }
  }

  async getDesignSystem(
    id: string,
  ): Promise<Result<DesignSystem | null>> {
    const sparql = `${NS}
      SELECT ?ds ?name WHERE {
        ?ds a cogo:DesignSystem ;
            cogo:designSystemId "${sanitizeLiteral(id)}" ;
            cogo:name ?name .
      }
      LIMIT 1
    `
    const { rows, durationMs } = await this.query(sparql)
    const data: DesignSystem | null = rows[0]
      ? { id, iri: rows[0].ds, name: rows[0].name }
      : null
    return { data, meta: { durationMs, source: "local" } }
  }

  async listDesignSystems(): Promise<Result<DesignSystem[]>> {
    const sparql = `${NS}
      SELECT ?ds ?id ?name WHERE {
        ?ds a cogo:DesignSystem ;
            cogo:designSystemId ?id ;
            cogo:name ?name .
      }
    `
    const { rows, durationMs } = await this.query(sparql)
    const data: DesignSystem[] = rows.map((row) => ({
      id: row.id,
      iri: row.ds,
      name: row.name,
    }))
    return { data, meta: { durationMs, source: "local" } }
  }

  async getSemanticTypes(
    ctx: { category?: string } = {},
  ): Promise<Result<SemanticType[]>> {
    const catFilter = ctx.category
      ? `FILTER (?cat = "${sanitizeLiteral(ctx.category)}")`
      : ""
    const sparql = `${NS}
      SELECT ?iri ?name ?cat WHERE {
        ?iri a cogo:SemanticType ;
             cogo:name ?name ;
             cogo:category ?cat .
        ${catFilter}
      }
    `
    const { rows, durationMs } = await this.query(sparql)
    const data: SemanticType[] = rows.map((row) => ({
      iri: row.iri,
      name: row.name,
      category: row.cat,
    }))
    return { data, meta: { durationMs, source: "local" } }
  }

  // ——— Vocabulary read surface (Studio Vocabulary panel) ———
  // SPARQL + row mappers live in vocabulary.ts (verbatim port of the
  // former cogo-studio kg-client localGet* bypass).

  async getExpressionPrefixes(): Promise<Result<ExpressionPrefixItem[]>> {
    const { rows, durationMs } = await this.query(EXPRESSION_PREFIX_SPARQL)
    return { data: mapExpressionPrefixRows(rows), meta: { durationMs, source: "local" } }
  }

  async getExpressionAccessors(): Promise<Result<ExpressionAccessorItem[]>> {
    const { rows, durationMs } = await this.query(EXPRESSION_ACCESSOR_SPARQL)
    return { data: mapExpressionAccessorRows(rows), meta: { durationMs, source: "local" } }
  }

  async getUiEventVocabulary(): Promise<Result<UiEventVocabularyItem[]>> {
    const { rows, durationMs } = await this.query(UI_EVENT_VOCABULARY_SPARQL)
    return { data: mapUiEventVocabularyRows(rows), meta: { durationMs, source: "local" } }
  }

  async getUiActionVocabulary(): Promise<Result<UiActionVocabularyItem[]>> {
    const { rows, durationMs } = await this.query(UI_ACTION_VOCABULARY_SPARQL)
    return { data: mapUiActionVocabularyRows(rows), meta: { durationMs, source: "local" } }
  }

  async getNarrative(
    ctx: { dsId: string },
  ): Promise<Result<Narrative | null>> {
    const dsId = sanitizeLiteral(ctx.dsId)
    const iri = `https://cogo.xyz/instances/narrative/${dsId}`
    const sparql = `${NS}
      SELECT ?iri ?vt ?cr ?dd ?apg ?rb WHERE {
        BIND(<${iri}> AS ?iri)
        ?iri a cogo:Narrative .
        OPTIONAL { ?iri cogo:visualTheme ?vt }
        OPTIONAL { ?iri cogo:colorRoles ?cr }
        OPTIONAL { ?iri cogo:dosDonts ?dd }
        OPTIONAL { ?iri cogo:agentPromptGuide ?apg }
        OPTIONAL { ?iri cogo:responsiveBehavior ?rb }
      }
      LIMIT 1
    `
    const { rows, durationMs } = await this.query(sparql)
    if (rows.length === 0) {
      return { data: null, meta: { durationMs, source: "local" } }
    }
    const row = rows[0]
    const data: Narrative = {
      iri: row.iri,
      dsId: ctx.dsId,
      visualTheme: row.vt || undefined,
      colorRoles: row.cr || undefined,
      dosDonts: row.dd || undefined,
      agentPromptGuide: row.apg || undefined,
      responsiveBehavior: row.rb || undefined,
    }
    return { data, meta: { durationMs, source: "local" } }
  }

  async getI18n(
    ctx: { locale: string },
  ): Promise<Result<I18n | null>> {
    const locale = sanitizeLiteral(ctx.locale)
    // 1. Pick the cogo:I18n instance whose supportedLocales contains the tag.
    // 2. Pull all 5 predicates in one round-trip; group supportedLocales via
    //    GROUP_CONCAT. defaultLocale is sh:maxCount 1 so a plain SAMPLE works.
    const sparql = `${NS}
      SELECT ?iri
             (GROUP_CONCAT(DISTINCT ?sl; separator="|") AS ?locales)
             (SAMPLE(?dl) AS ?defLocale)
             (SAMPLE(?lo) AS ?overrides)
             (SAMPLE(?nf) AS ?nfmt)
             (SAMPLE(?df) AS ?dfmt)
      WHERE {
        ?iri a cogo:I18n ;
             cogo:supportedLocales ?sl ;
             cogo:defaultLocale ?dl .
        ?iri cogo:supportedLocales "${locale}" .
        OPTIONAL { ?iri cogo:localeOverrides ?lo }
        OPTIONAL { ?iri cogo:numberFormats ?nf }
        OPTIONAL { ?iri cogo:dateFormats ?df }
      }
      GROUP BY ?iri
      LIMIT 1
    `
    const { rows, durationMs } = await this.query(sparql)
    if (rows.length === 0) {
      return { data: null, meta: { durationMs, source: "local" } }
    }
    const row = rows[0]
    const localesRaw = row.locales || ""
    const supportedLocales = localesRaw
      .split("|")
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 0)
    const data: I18n = {
      iri: row.iri,
      supportedLocales,
      defaultLocale: row.defLocale || "",
      localeOverrides: row.overrides || undefined,
      numberFormats: row.nfmt || undefined,
      dateFormats: row.dfmt || undefined,
    }
    return { data, meta: { durationMs, source: "local" } }
  }

  async getTokens(
    ctx: { id: string },
  ): Promise<Result<Tokens | null>> {
    const id = sanitizeLiteral(ctx.id)
    const iri = `https://cogo.xyz/instances/tokens/${id}`
    const sparql = `${NS}
      SELECT ?iri ?sp ?el ?mo ?bp ?co ?ra ?ty WHERE {
        BIND(<${iri}> AS ?iri)
        ?iri a cogo:Tokens .
        OPTIONAL { ?iri cogo:spacing ?sp }
        OPTIONAL { ?iri cogo:elevation ?el }
        OPTIONAL { ?iri cogo:motion ?mo }
        OPTIONAL { ?iri cogo:breakpoints ?bp }
        OPTIONAL { ?iri cogo:colors ?co }
        OPTIONAL { ?iri cogo:radius ?ra }
        OPTIONAL { ?iri cogo:typography ?ty }
      }
      LIMIT 1
    `
    const { rows, durationMs } = await this.query(sparql)
    if (rows.length === 0) {
      return { data: null, meta: { durationMs, source: "local" } }
    }
    const row = rows[0]
    const data: Tokens = {
      iri: row.iri,
      id: ctx.id,
      spacing: row.sp || undefined,
      elevation: row.el || undefined,
      motion: row.mo || undefined,
      breakpoints: row.bp || undefined,
      colors: row.co || undefined,
      radius: row.ra || undefined,
      typography: row.ty || undefined,
    }
    return { data, meta: { durationMs, source: "local" } }
  }

  async getTheme({
    themeId,
  }: {
    themeId: string
  }): Promise<{ theme: ThemeV2Record }> {
    // v1.0.10 contract: Theme is a composite. The shard declares
    // ctheme:<id> a cogo:Theme with typed ref edges
    // (cogo:tokensRef / cogo:narrativeRef / cogo:i18nRef /
    // cogo:atomDefaultsRef) pointing at the canonical sub-resources.
    //
    // Phase A: SPARQL-query the Theme instance to extract its ref IRIs +
    // metadata literals — we MUST NOT rely on string-convention lookups
    // (e.g. `${themeId}-tokens`) because the shard's refs point at IRIs
    // like ctok:default-tokens whose local name is independent of themeId.
    //
    // Phase B: resolve each ref by IRI via direct SPARQL. The existing
    // typed getters (getTokens/getNarrative/getI18n) accept id/dsId/locale
    // inputs and construct their own IRIs — so we bypass them and query
    // the loaded store directly by the IRI we just extracted.
    const themeIdLit = sanitizeLiteral(themeId)
    const themeSparql = `${NS}
      SELECT ?theme ?themeVersion ?tokensRef ?narrativeRef ?i18nRef ?atomsRef
             ?metadataName ?metadataDescription
      WHERE {
        ?theme a cogo:Theme ;
               cogo:themeId "${themeIdLit}" .
        OPTIONAL { ?theme cogo:themeVersion ?themeVersion }
        OPTIONAL { ?theme cogo:tokensRef ?tokensRef }
        OPTIONAL { ?theme cogo:narrativeRef ?narrativeRef }
        OPTIONAL { ?theme cogo:i18nRef ?i18nRef }
        OPTIONAL { ?theme cogo:atomDefaultsRef ?atomsRef }
        OPTIONAL { ?theme cogo:metadataName ?metadataName }
        OPTIONAL { ?theme cogo:metadataDescription ?metadataDescription }
      }
      LIMIT 1
    `
    const themeRes = await this.query(themeSparql)
    if (themeRes.rows.length === 0) {
      throw new Error(`unknown theme ${themeId}`)
    }
    const row = themeRes.rows[0]

    const safeJson = (raw: string | undefined): unknown => {
      if (!raw) return undefined
      try {
        return JSON.parse(raw)
      } catch {
        return raw
      }
    }

    // Phase B.1: resolve Tokens by IRI (dereferences ?tokensRef). The
    // cogo:Tokens instance carries 7 axis predicates — pull them in one
    // shot. `row.tokensRef` is the IRI (Oxigraph unwraps angle brackets).
    const primitives: Record<string, unknown> = {}
    if (row.tokensRef) {
      const tokensIri = sanitizeIri(row.tokensRef)
      const tokensSparql = `${NS}
        SELECT ?colors ?radius ?typography ?spacing ?elevation ?motion ?breakpoints
        WHERE {
          OPTIONAL { <${tokensIri}> cogo:colors ?colors }
          OPTIONAL { <${tokensIri}> cogo:radius ?radius }
          OPTIONAL { <${tokensIri}> cogo:typography ?typography }
          OPTIONAL { <${tokensIri}> cogo:spacing ?spacing }
          OPTIONAL { <${tokensIri}> cogo:elevation ?elevation }
          OPTIONAL { <${tokensIri}> cogo:motion ?motion }
          OPTIONAL { <${tokensIri}> cogo:breakpoints ?breakpoints }
        }
        LIMIT 1
      `
      const tokensRes = await this.query(tokensSparql).catch(() => null)
      const t = tokensRes?.rows[0]
      if (t) {
        const colors = safeJson(t.colors)
        if (colors !== undefined) primitives.colors = colors
        const spacing = safeJson(t.spacing)
        if (spacing !== undefined) primitives.spacing = spacing
        const elevation = safeJson(t.elevation)
        if (elevation !== undefined) primitives.elevation = elevation
        const motion = safeJson(t.motion)
        if (motion !== undefined) primitives.motion = motion
        const breakpoints = safeJson(t.breakpoints)
        if (breakpoints !== undefined) primitives.breakpoints = breakpoints
        const radius = safeJson(t.radius)
        if (radius !== undefined) primitives.radius = radius
        const typography = safeJson(t.typography)
        if (typography !== undefined) primitives.typography = typography
      }
    }

    // Phase B.2: resolve Narrative by IRI.
    let narrativeBlock: Record<string, unknown> | undefined
    if (row.narrativeRef) {
      const narrIri = sanitizeIri(row.narrativeRef)
      const narrSparql = `${NS}
        SELECT ?visualTheme ?colorRoles ?dosDonts ?agentPromptGuide ?responsiveBehavior
        WHERE {
          OPTIONAL { <${narrIri}> cogo:visualTheme ?visualTheme }
          OPTIONAL { <${narrIri}> cogo:colorRoles ?colorRoles }
          OPTIONAL { <${narrIri}> cogo:dosDonts ?dosDonts }
          OPTIONAL { <${narrIri}> cogo:agentPromptGuide ?agentPromptGuide }
          OPTIONAL { <${narrIri}> cogo:responsiveBehavior ?responsiveBehavior }
        }
        LIMIT 1
      `
      const narrRes = await this.query(narrSparql).catch(() => null)
      const n = narrRes?.rows[0]
      if (n && (n.visualTheme || n.colorRoles || n.dosDonts || n.agentPromptGuide || n.responsiveBehavior)) {
        narrativeBlock = {
          visualTheme: n.visualTheme || undefined,
          colorRoles: n.colorRoles || undefined,
          dosDonts: n.dosDonts || undefined,
          agentPromptGuide: n.agentPromptGuide || undefined,
          responsiveBehavior: n.responsiveBehavior || undefined,
        }
      }
    }

    // Phase B.3: resolve I18n by IRI. supportedLocales is multi-valued —
    // GROUP_CONCAT into a pipe-separated string then split on read.
    let i18nBlock: ThemeV2Record["i18n"] | undefined
    if (row.i18nRef) {
      const i18nIri = sanitizeIri(row.i18nRef)
      const i18nSparql = `${NS}
        SELECT (GROUP_CONCAT(DISTINCT ?sl; separator="|") AS ?locales)
               (SAMPLE(?dl) AS ?defLocale)
        WHERE {
          <${i18nIri}> cogo:supportedLocales ?sl .
          <${i18nIri}> cogo:defaultLocale ?dl .
        }
      `
      const i18nRes = await this.query(i18nSparql).catch(() => null)
      const i = i18nRes?.rows[0]
      if (i && (i.defLocale || i.locales)) {
        const supported = (i.locales || "")
          .split("|")
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
        i18nBlock = {
          defaultLocale: i.defLocale || "",
          supportedLocales: supported,
        }
      }
    }

    // Phase B.4: resolve atom defaults by IRI. The Pattern carries a
    // cogo:payload JSON literal — parse + shallow-validate the outer
    // shape so we only hand through well-formed object-of-objects.
    const atoms: Record<string, Record<string, unknown>> = {}
    if (row.atomsRef) {
      const atomsIri = sanitizeIri(row.atomsRef)
      const atomsSparql = `${NS}
        SELECT ?payload WHERE {
          <${atomsIri}> cogo:payload ?payload .
        }
        LIMIT 1
      `
      const atomsRes = await this.query(atomsSparql).catch(() => null)
      const payload = atomsRes?.rows[0]?.payload
      if (payload) {
        try {
          const parsed = JSON.parse(payload) as unknown
          if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
              if (v && typeof v === "object" && !Array.isArray(v)) {
                atoms[k] = v as Record<string, unknown>
              }
            }
          }
        } catch {
          // swallow — atoms stays empty on malformed JSON
        }
      }
    }

    // row.themeVersion is a Turtle literal read from the shard; the v1.0.10
    // contract pins ThemeV2Record.version to the literal "2.0.0" so we
    // narrow here and warn via the meta envelope on mismatch in future
    // iterations. Today only one Theme instance ships.
    return {
      theme: {
        $id: `cogo:Theme/${themeId}`,
        version: "2.0.0",
        primitives,
        atoms,
        narrative: narrativeBlock,
        i18n: i18nBlock,
      },
    }
  }

  async getPatterns(
    ctx: { domain?: string } = {},
  ): Promise<Result<Pattern[]>> {
    // Optional domain filter: case-insensitive substring on domainSemantics.
    // v1 has no structured `cogo:domain` predicate (spec §12 Q3 — defer).
    const filter = ctx.domain
      ? `FILTER (CONTAINS(LCASE(STR(?domainSemantics)), "${sanitizeLiteral(ctx.domain).toLowerCase()}"))`
      : ""
    const sparql = `${NS}
      SELECT ?iri ?patternId ?semanticRoot ?requiredChildren ?requiredBindings ?requiredEvents ?domainSemantics
      WHERE {
        ?iri a cogo:Pattern ;
             cogo:patternId ?patternId ;
             cogo:semanticRoot ?semanticRoot .
        OPTIONAL { ?iri cogo:requiredChildren ?requiredChildren }
        OPTIONAL { ?iri cogo:requiredBindings ?requiredBindings }
        OPTIONAL { ?iri cogo:requiredEvents ?requiredEvents }
        OPTIONAL { ?iri cogo:domainSemantics ?domainSemantics }
        ${filter}
      }
      ORDER BY ?patternId
    `
    const { rows, durationMs } = await this.query(sparql)
    const data: Pattern[] = rows.map((row) => ({
      iri: row.iri,
      patternId: row.patternId,
      semanticRoot: row.semanticRoot,
      requiredChildren: row.requiredChildren || undefined,
      requiredBindings: row.requiredBindings || undefined,
      requiredEvents: row.requiredEvents || undefined,
      domainSemantics: row.domainSemantics || undefined,
    }))
    return { data, meta: { durationMs, source: "local" } }
  }

  async getActions(
    filter: GetActionsFilter = {},
  ): Promise<Result<Action[]>> {
    const filters: string[] = []
    if (filter.category) {
      filters.push(`FILTER (?category = "${sanitizeLiteral(filter.category)}")`)
    }
    if (filter.tier) {
      filters.push(`FILTER (?tier = "${sanitizeLiteral(filter.tier)}")`)
    }
    if (filter.ids && filter.ids.length > 0) {
      const iris = filter.ids.map((i) => `<${sanitizeIri(i)}>`).join(", ")
      filters.push(`FILTER (?act IN (${iris}))`)
    }
    const sparql = `${NS}
      SELECT ?act ?name ?category ?tier ?plats WHERE {
        ?act a cogo:Action ;
             cogo:name ?name ;
             cogo:category ?category .
        OPTIONAL { ?act cogo:tier ?tier }
        OPTIONAL { ?act cogo:platforms ?plats }
        ${filters.join("\n        ")}
      }
    `
    const { rows, durationMs } = await this.query(sparql)
    const data: Action[] = rows.map((row) => ({
      iri: row.act,
      name: row.name,
      category: row.category,
      tier: row.tier || undefined,
      platforms: row.plats ? [row.plats] : undefined,
    }))
    return { data, meta: { durationMs, source: "local" } }
  }

  async getEvents(
    filter: GetEventsFilter = {},
  ): Promise<Result<Event[]>> {
    const filters: string[] = []
    if (filter.domain) {
      filters.push(`FILTER (?domain = "${sanitizeLiteral(filter.domain)}")`)
    }
    if (filter.emittedByComponent) {
      filters.push(
        `FILTER (?comp = <${sanitizeIri(filter.emittedByComponent)}>)`,
      )
    }
    if (filter.emittedByDatasource) {
      filters.push(
        `FILTER (?ds = <${sanitizeIri(filter.emittedByDatasource)}>)`,
      )
    }
    if (filter.emittedByAction) {
      filters.push(
        `FILTER (?act = <${sanitizeIri(filter.emittedByAction)}>)`,
      )
    }
    const sparql = `${NS}
      SELECT ?ev ?name ?domain ?comp ?ds ?act WHERE {
        ?ev a cogo:Event ;
            cogo:name ?name .
        OPTIONAL { ?ev cogo:domain ?domain }
        OPTIONAL { ?ev cogo:emittedByComponent  ?comp }
        OPTIONAL { ?ev cogo:emittedByDatasource ?ds }
        OPTIONAL { ?ev cogo:emittedByAction     ?act }
        ${filters.join("\n        ")}
      }
    `
    const { rows, durationMs } = await this.query(sparql)
    const data: Event[] = rows.map((row) => ({
      iri: row.ev,
      name: row.name,
      domain: row.domain || undefined,
      emittedBy: {
        component: row.comp || undefined,
        datasource: row.ds || undefined,
        action: row.act || undefined,
      },
    }))
    return { data, meta: { durationMs, source: "local" } }
  }

  // ——— Validation ———

  async validate(
    phase: ValidationPhase,
  ): Promise<Result<ValidationReport>> {
    await this.ensure()
    if (!this.mod) {
      throw new Error(
        "[@cogo/kg-repository] validate called without init",
      )
    }
    const rules = this.rules.rulesFor(phase)
    const report = this.mod.applyShacl(rules)
    const data: ValidationReport = {
      conforms: report.conforms,
      totalRules: report.totalRules,
      violations: report.violations.map((v) => ({
        shape: v.shape,
        focusClass: v.focusClass,
        severity: v.severity,
        message: v.message,
        bindings: v.bindings,
      })),
    }
    return { data, meta: { durationMs: report.durationMs, source: "local" } }
  }

  // ——— Write methods (Gap 2 MVP — observation buffer + best-effort/durable POST) ———

  async recordObservation(
    obs: Observation,
  ): Promise<Result<{ id: string }>> {
    const t0 = performance.now()
    const suffix = makeClientSessionId().replace(/-/g, "").slice(0, 16)
    const id = `cogo:obs/${suffix}`
    // MVP: local SPARQL INSERT deferred (KgBrowserModule exposes reads only);
    // keep in memory, server-side Oxigraph holds the durable copy.
    this.pendingObservations.push({ ...obs, syncStatus: "pending" })
    const durationMs = +(performance.now() - t0).toFixed(3)
    return { data: { id }, meta: { durationMs, source: "local" } }
  }

  private async postObservations(
    mode: "best-effort" | "durable",
    records: ObservationRecord[],
  ): Promise<Response> {
    const base = this.config.workerBaseUrl
    if (!base) {
      throw new Error(
        "[@cogo/kg-repository] workerBaseUrl not configured",
      )
    }
    const quads = records
      .map((r, i) => observationToNQuads(r, `cogo:obs/${r.clientSessionId}-${i}`))
      .join("")
    return fetch(`${base}/kg/observations`, {
      method: "POST",
      headers: {
        "content-type": "application/n-quads",
        "x-cogo-client-session": records[0]?.clientSessionId ?? "unknown",
        "x-cogo-sync-mode": mode,
      },
      body: quads,
    })
  }

  async syncObservations(): Promise<Result<SyncResult>> {
    const t0 = performance.now()
    const pending = this.pendingObservations.filter(
      (r) => r.syncStatus === "pending",
    )
    if (pending.length === 0) {
      const durationMs = +(performance.now() - t0).toFixed(3)
      return {
        data: { synced: 0, remaining: 0 },
        meta: { durationMs, source: "local" },
      }
    }
    const res = await this.postObservations("best-effort", pending)
    const durationMs = +(performance.now() - t0).toFixed(3)
    if (!res.ok) {
      throw new Error(
        `[@cogo/kg-repository] syncObservations: worker ${res.status}`,
      )
    }
    const syncedAt = new Date().toISOString()
    for (const r of pending) {
      r.syncStatus = "synced"
      r.syncedAt = syncedAt
    }
    const remaining = this.pendingObservations.filter(
      (r) => r.syncStatus === "pending",
    ).length
    return {
      data: { synced: pending.length, remaining },
      meta: { durationMs, source: "server" },
    }
  }

  async saveObservations(): Promise<Result<SaveResult>> {
    const t0 = performance.now()
    const toCommit = this.pendingObservations.filter(
      (r) => r.syncStatus !== "committed",
    )
    if (toCommit.length === 0) {
      const durationMs = +(performance.now() - t0).toFixed(3)
      return {
        data: { committed: 0, failed: [] },
        meta: { durationMs, source: "local" },
      }
    }
    try {
      const res = await this.postObservations("durable", toCommit)
      const durationMs = +(performance.now() - t0).toFixed(3)
      if (!res.ok) {
        return {
          data: {
            committed: 0,
            failed: toCommit.map((r) => r.clientSessionId),
          },
          meta: { durationMs, source: "server" },
        }
      }
      const committedAt = new Date().toISOString()
      for (const r of toCommit) {
        r.syncStatus = "committed"
        r.committedAt = committedAt
      }
      return {
        data: { committed: toCommit.length, failed: [] },
        meta: { durationMs, source: "server" },
      }
    } catch {
      const durationMs = +(performance.now() - t0).toFixed(3)
      return {
        data: {
          committed: 0,
          failed: toCommit.map((r) => r.clientSessionId),
        },
        meta: { durationMs, source: "server" },
      }
    }
  }

  async findObservations(
    query: FindObservationsQuery = {},
  ): Promise<Result<ObservationRecord[]>> {
    const t0 = performance.now()
    if (query.include === "cross-session") {
      const durationMs = +(performance.now() - t0).toFixed(3)
      return { data: [], meta: { durationMs, source: "local" } }
    }
    let records = this.pendingObservations
    if (query.anchor) {
      records = records.filter(
        (r) =>
          r.forSemanticType === query.anchor || r.forPattern === query.anchor,
      )
    }
    if (query.since) {
      records = records.filter((r) => r.observedAt >= query.since!)
    }
    const durationMs = +(performance.now() - t0).toFixed(3)
    return { data: [...records], meta: { durationMs, source: "local" } }
  }

  async registerExample(_args: {
    template: unknown
    bindingSchema: object
    semanticType: string
    dsId?: string
  }): Promise<Result<{ exampleId: string }>> {
    throw new Error(
      "[@cogo/kg-repository] registerExample requires server path — use HybridRepository",
    )
  }

  async upsertDesignSystem(
    _ds: DesignSystem,
  ): Promise<Result<{ dsId: string }>> {
    throw new Error(
      "[@cogo/kg-repository] upsertDesignSystem requires server path — use HybridRepository",
    )
  }

  // ——— Internals ———

  private safeParseJson(s: string): Record<string, unknown> | undefined {
    try {
      const parsed = JSON.parse(s) as unknown
      return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : undefined
    } catch {
      return undefined
    }
  }
}
